import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { BACKEND_URL } from "../../lib/api";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";

const resolveUrl = (raw) => {
    if (!raw) return "";
    if (raw.startsWith("/api/")) return `${BACKEND_URL}${raw}`;
    return raw;
};

export default function Artists() {
    const [artists, setArtists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");

    useEffect(() => {
        api.get("/artists")
            .then(({ data }) => setArtists(Array.isArray(data) ? data : []))
            .catch(() => setArtists([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return artists;
        return artists.filter((a) =>
            (a.name || "").toLowerCase().includes(q) ||
            (a.role || "").toLowerCase().includes(q) ||
            (a.credits || "").toLowerCase().includes(q)
        );
    }, [artists, query]);

    return (
        <div className="min-h-screen flex flex-col" data-testid="artists-page">
            <PublicHeader />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-avx-line">
                <div className="absolute inset-0 bg-avx-glow" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="max-w-3xl">
                        <div className="label-mono mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-avx-blue" />
                            AVX Pro Community
                        </div>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6">
                            Artists who <span className="text-avx-blue">trust</span> AVX Pro
                        </h1>
                        <p className="text-lg text-avx-muted leading-relaxed max-w-2xl">
                            Producers, mix engineers and artists who use our plugins in the studio, FOH and music production. This is their experience.
                        </p>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 w-full flex-1">
                {/* Search */}
                <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
                    <div className="label-mono">/ {filtered.length} artist{filtered.length === 1 ? "" : "s"}</div>
                    <div className="relative">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search artist, role or credit…"
                            className="bg-avx-bg border border-avx-line h-11 pl-4 pr-10 rounded-sm font-mono text-xs w-72 focus:outline-none focus:border-avx-blue transition-colors"
                            data-testid="artists-search-input"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-avx-muted text-xs">⌕</span>
                    </div>
                </div>

                {loading ? (
                    <div className="text-avx-muted text-center py-20 font-mono text-sm">Loading artists…</div>
                ) : filtered.length === 0 ? (
                    <EmptyState hasQuery={!!query} />
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="artists-grid">
                        {filtered.map((a) => (
                            <ArtistCard key={a.id} artist={a} />
                        ))}
                    </div>
                )}
            </section>

            <PublicFooter />
        </div>
    );
}

function ArtistCard({ artist }) {
    const photo = resolveUrl(artist.photo_url);
    const plugins = artist.favorite_plugins || [];

    return (
        <article
            className="panel-elevated overflow-hidden flex flex-col group transition-all duration-300 hover:border-avx-blue/50 hover:-translate-y-0.5"
            data-testid={`artist-card-${artist.slug}`}
        >
            {/* Photo */}
            <div className="aspect-[4/5] bg-avx-bg/60 border-b border-avx-line overflow-hidden relative">
                {photo ? (
                    <img
                        src={photo}
                        alt={artist.name}
                        loading="lazy"
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                        data-testid={`artist-photo-${artist.slug}`}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <span className="font-heading font-black text-7xl text-avx-line tracking-tighter">
                            {(artist.name || "?").charAt(0).toUpperCase()}
                        </span>
                    </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-avx-bg via-avx-bg/60 to-transparent pointer-events-none" />
            </div>

            {/* Body */}
            <div className="p-6 flex flex-col flex-1">
                <h3 className="font-heading font-black text-2xl tracking-tight mb-1">{artist.name}</h3>
                {artist.role && (
                    <div className="text-sm text-avx-muted mb-3" data-testid={`artist-role-${artist.slug}`}>
                        {artist.role}
                    </div>
                )}
                {artist.credits && (
                    <div className="font-bold text-sm text-avx-text mb-4 leading-snug">{artist.credits}</div>
                )}
                {artist.testimonial && (
                    <p className="text-sm text-avx-text/85 leading-relaxed mb-5 italic">
                        “{artist.testimonial}”
                    </p>
                )}

                {plugins.length > 0 && (
                    <div className="mt-auto pt-4 border-t border-avx-line">
                        <div className="label-mono text-[9px] mb-2">/ Favorite plugins</div>
                        <div className="flex flex-wrap gap-1.5">
                            {plugins.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/plugins/${p.slug}`}
                                    className="px-2 py-1 rounded-sm bg-avx-blue/10 border border-avx-blue/30 text-avx-blue text-[10px] font-mono uppercase tracking-[0.15em] hover:bg-avx-blue/20 transition-colors"
                                    data-testid={`artist-plugin-${artist.slug}-${p.slug}`}
                                >
                                    {p.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
}

function EmptyState({ hasQuery }) {
    return (
        <div className="panel p-12 text-center" data-testid="artists-empty">
            <div className="label-mono mb-4">/ {hasQuery ? "No results" : "Coming soon"}</div>
            <p className="text-avx-muted max-w-md mx-auto leading-relaxed">
                {hasQuery
                    ? "We couldn't find artists for that search. Try another name or role."
                    : "We haven't published artist profiles yet. Come back soon to meet the producers and engineers who trust AVX Pro."}
            </p>
        </div>
    );
}
